import type { KnockGuideStep } from "@knocklabs/client";
import { useGuides } from "@knocklabs/react";
import { CbhFeatureFlag } from "@src/appV2/FeatureFlags/CbhFeatureFlag";
import { useCbhFlag } from "@src/appV2/FeatureFlags/useCbhFlag";
import { KnockErrorBoundary } from "@src/appV2/Knock/components/KnockErrorBoundary";
import { useKnockMarkAsSeen } from "@src/appV2/Knock/hooks/useKnockMarkAsSeen";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { ModalStatus, useModalState } from "@src/lib/ui-react";
import { useEffect, useRef } from "react";

import { REVIEW_PROMPT_GUIDE_TYPE } from "./constants";
import { ReviewPromptGuideView, type ReviewPromptGuideViewContent } from "./ReviewPromptGuideView";
import { useRequestInAppReview } from "./useRequestInAppReview";

interface KnockReviewPromptContent {
  title?: string;
  body?: string;
  yesButtonText?: string;
  noButtonText?: string;
  laterButtonText?: string;
}

function normalizeContent(
  content?: KnockReviewPromptContent
): ReviewPromptGuideViewContent | undefined {
  const title = content?.title?.trim();
  const body = content?.body?.trim();
  const yesButtonText = content?.yesButtonText?.trim();
  const noButtonText = content?.noButtonText?.trim();
  const laterButtonText = content?.laterButtonText?.trim();

  if (!title || !yesButtonText || !noButtonText || !laterButtonText) {
    return undefined;
  }

  return { title, body, yesButtonText, noButtonText, laterButtonText };
}

interface ReviewPromptItemProps {
  content: ReviewPromptGuideViewContent;
  guideKey: string;
  step: KnockGuideStep;
  workplaceId?: string;
  workplaceName?: string;
}

function ReviewPromptItem(props: ReviewPromptItemProps) {
  const { content, guideKey, step, workplaceId, workplaceName } = props;
  const { requestInAppReview } = useRequestInAppReview();
  const modalState = useModalState(ModalStatus.OPEN);

  useKnockMarkAsSeen(step);

  const hasLoggedViewed = useRef<string>();
  useEffect(() => {
    if (!hasLoggedViewed.current || hasLoggedViewed.current !== guideKey) {
      logEvent(APP_V2_USER_EVENTS.RATING_PROMPT_SHOWN, { guideKey, workplaceId, workplaceName });
      hasLoggedViewed.current = guideKey;
    }
  }, [guideKey, workplaceId, workplaceName]);

  return (
    <ReviewPromptGuideView
      content={content}
      modalState={modalState}
      onYesClick={async () => {
        step.markAsInteracted({ metadata: { action: "yes" } });
        logEvent(APP_V2_USER_EVENTS.RATING_PROMPT_YES_CLICKED, {
          guideKey,
          workplaceId,
          workplaceName,
        });
        // Close the dialog before kicking off the native call so the user
        // doesn't see our dialog and the native sheet stacked. The native
        // sheet animates in over the WebView and `requestInAppReview` swallows
        // its own failures, so archiving after the await is safe.
        modalState.closeModal();
        await requestInAppReview();
        step.markAsArchived();
      }}
      onNoClick={() => {
        step.markAsInteracted({ metadata: { action: "no" } });
        logEvent(APP_V2_USER_EVENTS.RATING_PROMPT_NO_CLICKED, {
          guideKey,
          workplaceId,
          workplaceName,
        });
        modalState.closeModal();
        step.markAsArchived();
      }}
      onLaterClick={() => {
        step.markAsInteracted({ metadata: { action: "later" } });
        logEvent(APP_V2_USER_EVENTS.RATING_PROMPT_LATER_CLICKED, {
          guideKey,
          workplaceId,
          workplaceName,
        });
        modalState.closeModal();
        step.markAsArchived();
      }}
    />
  );
}

interface ReviewPromptGuideInnerProps {
  workplaceId?: string;
  workplaceName?: string;
}

function ReviewPromptGuideInner(props: ReviewPromptGuideInnerProps) {
  const { workplaceId, workplaceName } = props;
  // Kill-switch gate at the root of the surface so the dialog never mounts
  // when the flag is off — even if a Knock dashboard guide somehow targets
  // through. The hook holds the belt-and-suspenders check for the native call.
  const isEnabled = useCbhFlag(CbhFeatureFlag.WORKPLACE_RATING_PROMPT, { defaultValue: false });
  const { guides } = useGuides({ type: REVIEW_PROMPT_GUIDE_TYPE });

  if (!isEnabled || guides.length === 0) {
    return null;
  }

  const guide = guides[0];
  const step = guide?.getStep();
  const content = normalizeContent(step?.content as KnockReviewPromptContent | undefined);

  if (!guide || !step || !content) {
    return null;
  }

  return (
    <ReviewPromptItem
      content={content}
      guideKey={guide.key}
      step={step}
      workplaceId={workplaceId}
      workplaceName={workplaceName}
    />
  );
}

interface ReviewPromptGuideProps {
  /**
   * Active workplace context for analytics. Sourced by the parent container
   * from session state (workplace user id and profile name) so V2 stays
   * Redux-free.
   */
  workplaceId?: string;
  workplaceName?: string;
}

/**
 * App-root rating-prompt surface. Filters Knock guides on the custom
 * `review_prompt` type and renders the three-button mood step.
 *
 * ORB-2174 wires only the Yes path end-to-end (native review sheet via
 * `useRequestInAppReview`). No and Later paths archive the step; their
 * feedback + cooldown semantics arrive in ORB-2176.
 */
export function ReviewPromptGuide(props: ReviewPromptGuideProps) {
  const { workplaceId, workplaceName } = props;
  return (
    <KnockErrorBoundary>
      <ReviewPromptGuideInner workplaceId={workplaceId} workplaceName={workplaceName} />
    </KnockErrorBoundary>
  );
}
